/**
 * In-browser Crown DSL inference — the docs-site APP-LAYER adapter over the
 * shared onnxruntime-web client in `@crown/soma/dsl-web`.
 *
 * The whole client (model download with progress, Cache-API persistence,
 * WebGPU→WASM fallback, and the greedy cached-step decode via
 * `@crown/soma/byte-decode`) lives in the shared package and is NOT
 * forked here. This module only binds the docs-site's own asset locations:
 * where rsbuild copies the INT8 model and the onnxruntime-web WASM/JSEP runtime
 * assets (rsbuild.config.ts `output.copy`). Config flows in from the app layer
 * via import.meta.env (rsbuild define) — packages never read env.
 */
import {
  type DslEngine,
  type LoadEngineOptions,
  loadDslEngine as loadSharedDslEngine,
} from '@crown/soma/dsl-web';

export type {
  DslBackend,
  DslEngine,
  DslInferenceResult,
  LoadEngineOptions,
  LoadProgress,
} from '@crown/soma/dsl-web';

/** Where the ~44MB INT8 model is fetched from (app layer injects via rsbuild
 *  define; packages never read env). Default: served from the docs-site public
 *  path (rsbuild copies it there in dev + build). */
const MODEL_URL: string =
  (import.meta.env as Record<string, string>).DSL_MODEL_URL ??
  '/models/crown-dsl.step.int8.onnx';
/** Folder the onnxruntime-web WASM/JSEP assets are served from (rsbuild copies
 *  `ort-wasm-simd-threaded*.{wasm,mjs}` there). */
const ORT_WASM_PATH = '/ort/';

/** Download + prepare the in-browser crown-dsl engine with the docs-site's own
 *  asset locations. Real inference — the returned engine runs the production
 *  model's bytes in the browser. */
export function loadDslEngine(
  opts: LoadEngineOptions = {},
): Promise<DslEngine> {
  return loadSharedDslEngine(
    { modelUrl: MODEL_URL, wasmPaths: ORT_WASM_PATH },
    opts,
  );
}
